import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { FaUpload, FaSearch, FaChevronDown } from "react-icons/fa";
import { applyTheme } from "../utils/theme";
import Sidebar from "../components/common/Sidebar";
import Navbar from "../components/common/Navbar";
import SideModal from "../components/common/SideModal";
import MapWithSearch from "../components/common/MapWithSearch";
import { useApi } from "../hooks/useApi";
import DataTable from "../components/common/DataTable";

const CatchUpsManagement = () => {
  const { t, i18n } = useTranslation();
  const mode = useSelector((state) => state.theme.mode);
  const { uploadFile, addCatchUp, getCatchUps, deleteCatchUp, updateCatchup } = useApi();
  const isArabic = i18n.language === "ar";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [location, setLocation] = useState({ lat: 25.2048, lng: 55.2708 });
  const [allCategories, setAllCategories] = useState([]); // Store all data for frontend filtering
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOption, setSearchOption] = useState("name");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);


  const [formData, setFormData] = useState({
    title: "",
    category: "",
    fromDate: "",
    toDate: "",
    fromTime: "",
    toTime: "",
    contact: "",
    link: ""
  });

  const fileInputRef = useRef(null);

  // Utility functions for date and time formatting
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const twelveHour = hour % 12 || 12;
    return `${twelveHour}:${minutes} ${ampm}`;
  };

  const convert12HourTo24Hour = (time12h) => {
    if (!time12h) return "";
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }

    if (modifier === 'PM') {
      hours = parseInt(hours, 10) + 12;
    }

    return `${String(hours).padStart(2, '0')}:${minutes}`;
  };

  const parseDDMMYYYYToDate = (dateString) => {
    if (!dateString) return "";
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    // If it's already in YYYY-MM-DD format, return as is
    if (dateString.includes('-')) return dateString;
    // If it's in DD/MM/YYYY format, convert to YYYY-MM-DD
    return parseDDMMYYYYToDate(dateString);
  };

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "auto";
  }, [modalOpen]);

  // const handleAddCatchUp = () => setModalOpen(true);

  const handleAddCatchUp = () => {
    setIsEditing(false);
    setEditingItem(null);

    setFormData({
      title: "",
      category: "",
      fromDate: "",
      toDate: "",
      fromTime: "",
      toTime: "",
      contact: "",
      link:""
    });

    setImageUrl(null);
    setLocation({ lat: 25.2048, lng: 55.2708 });
    setModalOpen(true);
  };


  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error("Please select an image file");
      return;
    }

    try {
      message.loading("Uploading image...", 0);
      const response = await uploadFile(file);
      message.destroy();

      if (response.success || response.data) {
        const uploadedUrl = response.file?.fileUrl || response.url || response.data;
        setImageUrl(uploadedUrl);
        message.success("Image uploaded successfully!");
      } else {
        message.error("Failed to upload image");
      }
    } catch (error) {
      message.destroy();
      message.error("Error uploading image");
      console.error(error);
    }
  };

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));


  const handleSave = async () => {
    // Validate time order
    if (formData.fromTime && formData.toTime) {
      const fromTime24 = convert12HourTo24Hour(formData.fromTime);
      const toTime24 = convert12HourTo24Hour(formData.toTime);

      if (formData.fromDate === formData.toDate && fromTime24 >= toTime24) {
        message.error("End time must be after start time");
        return;
      }
    }




    // Required fields check
    if (
      !formData.title ||
      !formData.category ||
      !formData.fromDate ||
      !formData.toDate ||
      !formData.fromTime ||
      !formData.toTime ||
      !formData.contact ||
      !formData.link
    ) {
      message.error("Please fill in all fields before saving");
      return;
    }

    // ✅ For adding new record, image is mandatory
    // ✅ For editing, we skip image requirement (user may not change it)
    if (!isEditing && !imageUrl) {
      message.error("Please upload an image before saving");
      return;
    }

    // Format date/time strings
    const formattedFromDate = formatDateToDDMMYYYY(formData.fromDate);
    const formattedToDate = formatDateToDDMMYYYY(formData.toDate);
    const formattedFromTime = formData.fromTime;
    const formattedToTime = formData.toTime;

    // Payload for API
    const payload = {
      title: formData.title,
      image: imageUrl || editingItem?.image, // keep old image if editing
      category: formData.category,
      dates: `${formattedFromDate} - ${formattedToDate}`,
      times: `${formattedFromTime} - ${formattedToTime}`,
      latitude: location.lat,
      longitude: location.lng,
      moreInfo: formData.contact,
       link: formData.link,
      isActive: true,
    };

    try {
      let response;

      if (isEditing && editingItem?._id) {
        // 🔧 EDIT FLOW — requires updateCatchUp in your useApi hook
        response = await updateCatchup(editingItem._id, payload);
      } else {
        // 🆕 ADD FLOW
        response = await addCatchUp(payload);
      }

      if (response.success) {
        message.success(
          isEditing
            ? "Catch Up updated successfully!"
            : "Catch Up saved successfully!"
        );

        // Reset state
        setFormData({
          title: "",
          category: "",
          fromDate: "",
          toDate: "",
          fromTime: "",
          toTime: "",
          contact: "",
          link:""
        });
        setImageUrl(null);
        setLocation({ lat: 25.2048, lng: 55.2708 });
        setModalOpen(false);
        setIsEditing(false);
        setEditingItem(null);

        // Refresh table
        setSearchTrigger((prev) => prev + 1);

        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        message.error(response.message || "Failed to save Catch Up");
      }
    } catch (error) {
      console.error(error);
      message.error(
        isEditing ? "Error updating Catch Up" : "Error saving Catch Up"
      );
    }
  };

  const handleEdit = (item) => {
  setIsEditing(true);
  setEditingItem(item);

  // --- Parse dates ---
  let fromDateStr = "";
  let toDateStr = "";
  if (item.dates) {
    const parts = item.dates.split(" - ");
    fromDateStr = parts[0]?.trim() || "";
    toDateStr = parts[1]?.trim() || "";
  }

  // Convert DD/MM/YYYY → YYYY-MM-DD for date inputs
  const formatToInputDate = (dateStr) => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  // --- Parse times ---
  let fromTimeStr = "";
  let toTimeStr = "";
  if (item.times) {
    const parts = item.times.split(" - ");
    fromTimeStr = parts[0]?.trim() || "";
    toTimeStr = parts[1]?.trim() || "";
  }

  setFormData({
    title: item.title || "",
    category: item.category || "",
    fromDate: formatToInputDate(fromDateStr),
    toDate: formatToInputDate(toDateStr),
    fromTime: fromTimeStr,
    toTime: toTimeStr,
    contact: item.moreInfo || "",
     link: item.link || "",
  });

  // --- Location ---
  setLocation({
    lat: item.latitude ?? 25.2048,
    lng: item.longitude ?? 55.2708,
  });

  // --- Skip image ---
  setImageUrl(null);
  setModalOpen(true);
};

  const today = new Date().toISOString().split("T")[0];

  const handleDateChange = (field, value) => {
    if (field === "fromDate" && new Date(value) < new Date(today)) {
      message.warning("Start date cannot be in the past");
      return;
    }
    if (field === "toDate" && new Date(value) < new Date(formData.fromDate)) {
      message.warning("End date cannot be before start date");
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTimeChange = (field, value) => {
    // Convert 12-hour format to 24-hour for validation
    const time24 = convert12HourTo24Hour(value);

    if (field === "toTime" && formData.fromTime) {
      const fromTime24 = convert12HourTo24Hour(formData.fromTime);

      if (formData.fromDate === formData.toDate && time24 <= fromTime24) {
        message.warning("End time must be after start time");
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Generate time options in 12-hour format with 1-hour intervals (shorter list)
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      // Only include :00 times (1-hour intervals instead of 30-minute)
      const time24 = `${String(hour).padStart(2, '0')}:00`;
      const time12 = formatTimeTo12Hour(time24);
      times.push(time12);
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const CATEGORY_LABELS = {
  special_offers: "catchUps.categories.specialOffers",
  where_to_go: "catchUps.categories.whereToGo",
  activities: "catchUps.categories.activities",
  sports: "catchUps.categories.sports",
  special_deals: "catchUps.categories.specialDeals",
};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = [
    {
      key: "title",
      title: t("table.categoryName"),
      render: (val) => (
        <span className="text-text-tablebody font-semibold">{val || "-"}</span>
      ),
    },
    {
      key: "category",
      title: t("table.category"),
      render: (val) => (
        // <span className="text-text-tablebody">{val || "-"}</span>

         <span className="text-text-tablebody">
      {t(CATEGORY_LABELS[val] || val || "-")}
    </span>
      ),
    },
    {
      key: "dates",
      title: t("table.date"),
      render: (val) => (
        <span className="text-text-tablebody">{val || "-"}</span>
      ),
    },
    {
      key: "times",
      title: t("table.time"),
      render: (val) => (
        <span className="text-text-tablebody">{val || "-"}</span>
      ),
    },
    {
      key: "action",
      title: t("table.delete"),
    },
  ];

  // Filter data based on search term - useMemo to prevent unnecessary recalculations
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return allCategories;

    const searchLower = searchTerm.toLowerCase().trim();

    switch (searchOption) {
      case "name":
        return allCategories.filter(item =>
          item.title?.toLowerCase().includes(searchLower)
        );
      default:
        return allCategories;
    }
  }, [allCategories, searchTerm, searchOption]);

  // Calculate current page data - useMemo to prevent unnecessary recalculations
  const currentPageData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredCategories.slice(startIndex, endIndex);
  }, [filteredCategories, pagination.currentPage, pagination.itemsPerPage]);

  // Update pagination when filtered data changes
  useEffect(() => {
    const totalItems = filteredCategories.length;
    const totalPages = Math.ceil(totalItems / pagination.itemsPerPage) || 1;

    setPagination(prev => ({
      ...prev,
      totalPages,
      totalItems,
      // Reset to first page if current page is beyond new total pages
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  }, [filteredCategories.length, pagination.itemsPerPage]);

  // Fetch all data initially
  useEffect(() => {
    const fetchCatchUps = async () => {
      try {
        const response = await getCatchUps(
          1,
          1000,
          0,
          true,
          {}
        );

        console.log("CATCH UP RESPONSE : ", response);
        if (response.data && response.data.catchupLocations) {
          setAllCategories(response.data.catchupLocations);
        } else {
          message.error("Failed to fetch categories");
        }
      } catch (error) {
        message.error("Error fetching categories");
        console.error(error);
      }
    };
    fetchCatchUps();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDelete = async (id) => {
    try {
      const response = await deleteCatchUp(id);
      if (response.success) {
        message.success("Category deleted successfully");
        setAllCategories((prev) => prev.filter((cat) => cat._id !== id));
      } else {
        message.error(response.message || "Failed to delete category");
      }
    } catch (error) {
      message.error("Error deleting category");
    }
  };

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Reset to first page when search term changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const getSearchOptionLabel = (option) => {
    switch (option) {
      case "name":
        return t("search.byName");
      default:
        return t("search.byName");
    }
  };


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columnsWithActions = useMemo(
    () =>
      columns.map((col) =>
        col.key === "action"
          ? {
            ...col,
            render: (_, row) => (
              <div className="flex justify-center items-center gap-3">
                <button
                  className="text-text-tablebody hover:underline"
                  onClick={() => handleEdit(row)}
                >
                  Edit
                </button>

                <button
                  className="text-red-600 hover:underline"
                  onClick={() => handleDelete(row?._id)}
                >
                  Delete
                </button>
              </div>
            ),
          }
          : col
      ),
    [columns, handleDelete]
  );

  return (
    <div className="min-h-screen bg-background-main">
      <Navbar
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />

      <div className="flex flex-col lg:flex-row">
        <Sidebar
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />

        <main className="flex-1 p-4 lg:p-6 xl:p-8 w-full">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-semibold text-text-primary">{t("catchUps.addTitle")}</h1>
            <button
              onClick={handleAddCatchUp}
              className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors font-semibold"
            >
              {t("catchUps.addTitle")}
            </button>
          </div>

          {/* Header and Filters */}
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-0 justify-start mb-4">
            {/* Search Input */}
            <div className="flex items-center bg-background-search rounded-full sm:rounded-l-full sm:rounded-r-none px-4 py-2 w-full sm:max-w-sm border border-border-search">
              <FaSearch className="text-[#81879D] mr-2" />
              <input
                type="text"
                placeholder={t("search.searchCatchUp")}
                value={searchTerm}
                onChange={handleSearchChange}
                className="bg-transparent outline-none flex-1 text-input-text placeholder-[#81879D]"
              />
            </div>

            {/* Dropdown Filter */}
            <div
              className="relative flex items-center bg-background-search rounded-full sm:rounded-r-full sm:rounded-l-none px-4 py-2 w-full sm:w-auto sm:min-w-[120px] border-l border-l-border-search sm:border-l-searchbar-divider border-r border-r-border-search border-y border-y-border-search cursor-pointer"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span className="text-[#A9AEBC] mr-2">
                {getSearchOptionLabel(searchOption)}
              </span>
              <FaChevronDown
                className={`text-[#81879D] ml-auto transition-transform ${dropdownOpen ? "rotate-180" : ""
                  }`}
              />

              {/* Dropdown Options */}
              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-background-search text-[#A9AEBC] rounded-b-lg shadow-lg z-10 mt-1">
                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      setSearchOption("name");
                      setDropdownOpen(false);
                    }}
                  >
                    {t("search.byName")}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-background-card rounded-b-lg shadow-md">
            <DataTable columns={columnsWithActions} data={currentPageData} />
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 text-text-primary text-sm">
            <span>
              {t("pagination.showing")}{" "}
              {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}-
              {Math.min(
                pagination.currentPage * pagination.itemsPerPage,
                pagination.totalItems
              )}{" "}
              {t("pagination.of")} {pagination.totalItems}
            </span>

            <div className="flex items-center gap-1">
              <button
                className={`px-3 py-1 rounded ${pagination.currentPage === 1
                  ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                  : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                  }`}
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                {t("pagination.first")}
              </button>
              <button
                className={`px-3 py-1 rounded ${pagination.currentPage === 1
                  ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                  : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                  }`}
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                {t("pagination.back")}
              </button>

              {Array.from(
                { length: Math.min(5, pagination.totalPages) },
                (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      className={`px-3 py-1 rounded ${pagination.currentPage === pageNum
                        ? "bg-accent text-white font-bold"
                        : "bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                        }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                }
              )}

              {pagination.totalPages > 5 && (
                <>
                  <span className="px-2">...</span>
                  <button
                    className="px-3 py-1 rounded bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                    onClick={() => handlePageChange(pagination.totalPages)}
                  >
                    {pagination.totalPages}
                  </button>
                </>
              )}

              <button
                className={`px-3 py-1 rounded ${pagination.currentPage === pagination.totalPages
                  ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                  : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                  }`}
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                {t("pagination.next")}
              </button>
              <button
                className={`px-3 py-1 rounded ${pagination.currentPage === pagination.totalPages
                  ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                  : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                  }`}
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.totalPages)}
              >
                {t("pagination.last")}
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Side Modal */}
      <SideModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t("catchUps.addTitle", "Add Catch Up")}
        actions={
          <div className="flex gap-4 items-center">
            <button
              className="w-full py-3 rounded-xl bg-accent text-white font-semibold text-lg"
              onClick={handleSave}
            >
              {t("catchUps.save", "Save")}
            </button>
            <button
              className="w-full py-3 rounded-xl text-text-primary font-semibold text-lg bg-transparent mt-2"
              onClick={() => setModalOpen(false)}
            >
              {t("catchUps.cancel", "Cancel")}
            </button>
          </div>
        }
      >
        <div className="space-y-6 overflow-visible">
          {/* Map Section */}
          <div className="rounded-lg overflow-hidden border-gray-300 h-[300px]">
            <MapWithSearch onLocationSelect={(loc) => setLocation(loc)} />
          </div>

          {/* Upload Section */}
          <div className="mt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageSelect}
            />
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-accent transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <FaUpload className="mx-auto text-3xl text-gray-400 mb-2" />
              <p className="text-gray-600">{t("categoryDetail.upload")}</p>
              <p className="text-sm text-gray-500 mt-1">
                {t("categoryDetail.uploadTagline")}
              </p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-text-primary font-medium mb-1">
                {t("catchUps.title", "Title")}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder={t("catchUps.titlePlaceholder", "Enter title")}
                className={`w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none ${isArabic ? "text-sm" : "text-base"
                  }`}
              />
            </div>

            <div>
              <label className="block text-text-primary font-medium mb-1">
                {t("catchUps.category", "Category")}
              </label>
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 ${isArabic ? "font-arabic text-[15px]" : "text-base"
                  }`}
              >
                {[
                  { value: "special_offers", label: t("catchUps.categories.specialOffers", "Special Offers") },
                  { value: "where_to_go", label: t("catchUps.categories.whereToGo", "Where to go") },
                  { value: "activities", label: t("catchUps.categories.activities", "Activities") },
                  { value: "sports", label: t("catchUps.categories.sports", "Sports") },
                  { value: "special_deals", label: t("catchUps.categories.specialDeals", "Special Deals") },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center justify-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors
          ${formData.category === option.value
                        ? "border-accent bg-accent/10"
                        : "border-gray-300 hover:border-accent/60"
                      }
          ${isArabic ? "flex-row-reverse text-right" : ""}
        `}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={option.value}
                      checked={formData.category === option.value}
                      onChange={(e) => handleChange("category", e.target.value)}
                      className="w-5 h-5 accent-[var(--accent-color,#4f46e5)] cursor-pointer"
                    />
                    <span className="text-text-primary">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div>
              <label className="block text-text-primary font-medium mb-1">
                {t("catchUps.dates", "Dates")}
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm text-text-secondary mb-1">
                    {t("catchUps.fromDate", "From Date")}
                  </label>
                  <input
                    type="date"
                    min={today}
                    value={formatDateForInput(formData.fromDate)}
                    onChange={(e) => handleDateChange("fromDate", e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-text-secondary mb-1">
                    {t("catchUps.toDate", "To Date")}
                  </label>
                  <input
                    type="date"
                    min={formatDateForInput(formData.fromDate) || today}
                    value={formatDateForInput(formData.toDate)}
                    onChange={(e) => handleDateChange("toDate", e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Times */}
            {/* Times */}
            <div>
              <label className="block text-text-primary font-medium mb-1">
                {t("catchUps.times", "Times")}
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm text-text-secondary mb-1">
                    {t("catchUps.fromTime", "From Time")}
                  </label>
                  <div className="relative">
                    <select
                      value={formData.fromTime}
                      onChange={(e) => handleTimeChange("fromTime", e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none appearance-none pr-10"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.75rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '16px 12px'
                      }}
                    >
                      <option value="">{t("catchUps.selectStartTime", "Select start time")}</option>
                      {timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-text-secondary mb-1">
                    {t("catchUps.toTime", "To Time")}
                  </label>
                  <div className="relative">
                    <select
                      value={formData.toTime}
                      onChange={(e) => handleTimeChange("toTime", e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none appearance-none pr-10"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.75rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '16px 12px'
                      }}
                      disabled={!formData.fromTime}
                    >
                      <option value="">{t("catchUps.selectEndTime", "Select end time")}</option>
                      {timeOptions.map((time) => {
                        if (formData.fromTime && formData.fromDate === formData.toDate) {
                          const fromTime24 = convert12HourTo24Hour(formData.fromTime);
                          const toTime24 = convert12HourTo24Hour(time);
                          if (toTime24 <= fromTime24) return null;
                        }
                        return (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        );
                      }).filter(Boolean)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <label className="block text-text-primary font-medium mb-1">
                {t("catchUps.contact", "Contact")}
              </label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => handleChange("contact", e.target.value)}
                placeholder={t("catchUps.contactPlaceholder", "tel:+971501234567")}
                className={`w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none ${isArabic ? "text-sm" : "text-base"
                  }`}
              />
            </div>

             <div>
              <label className="block text-text-primary font-medium mb-1">
                {t("catchUps.link", "Link")}
              </label>
              <input
                type="text"
                value={formData.link}
                onChange={(e) => handleChange("link", e.target.value)}
                placeholder={t("catchUps.linkPlaceholder", "https://www.xyz.com")}
                className={`w-full p-3 rounded-lg border border-gray-300 bg-background-card text-text-primary focus:outline-none ${isArabic ? "text-sm" : "text-base"
                  }`}
              />
            </div>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default CatchUpsManagement;